import React from 'react';
import Icon from '../../components/Icon';
import Signal from './Signal';
import SameLANBadge from '../../components/SameLANBadge';
import './DeviceRow.css';

// "just now" / "Nm ago" / "Nh ago" — no library, no locale, no
// "5 minutes ago" verbosity. Matches the design's `iPhone 15 · 2m away`
// style strings (digit + single letter unit).
function formatRelativeTime(lastSeenMs) {
  if (!Number.isFinite(lastSeenMs)) return null;
  const deltaSec = Math.max(0, Math.round((Date.now() - lastSeenMs) / 1000));
  if (deltaSec < 30) return 'just now';
  if (deltaSec < 90) return '1m ago';
  if (deltaSec < 60 * 60) return `${Math.round(deltaSec / 60)}m ago`;
  if (deltaSec < 24 * 60 * 60) return `${Math.round(deltaSec / 3600)}h ago`;
  return `${Math.round(deltaSec / 86400)}d ago`;
}

function describeDevice(peer) {
  const kind =
    peer.deviceType === 'monitor' ? 'Desktop' : 'Phone';
  const rel = formatRelativeTime(peer.lastSeenMs);
  // No lastSeen → just the kind. With lastSeen → "Phone · 2m ago".
  return rel ? `${kind} · ${rel}` : kind;
}

/**
 * F10.1 — single nearby-device row.
 *
 * Mirrors `design/sharing-screens.jsx` lines 156-162: a 38px icon tile, a
 * two-line meta block (handle + device description), a 4-bar Signal, and a
 * mint-green `.connect` button that fires the Beam action.
 *
 * Props:
 *  - `peer`     — Peer from useNearbyPeers (userId, handle, deviceType,
 *                 sameLAN, signalBars, lastSeenMs).
 *  - `selected` — when true, marks the row as the active Beam target
 *                 (mint border, matches ShareSheet selected-row treatment).
 *  - `onSelect` — `(userId) => void`, fires on row click. Optional.
 *  - `onBeam`   — `(userId) => void`, fires on the `Beam` button. The
 *                 button stops propagation so a tap there doesn't also
 *                 trigger `onSelect`.
 *  - `deviceDescription` — override the auto-generated "Phone · 2m ago"
 *                 string (the design preview hardcodes richer text like
 *                 "iPhone 15 · 2m away" — feature-pass callers can pass
 *                 their own).
 */
const DeviceRow = ({
  peer,
  selected = false,
  onSelect,
  onBeam,
  deviceDescription,
}) => {
  if (!peer) return null;

  const description = deviceDescription ?? describeDevice(peer);
  const iconName = peer.deviceType === 'monitor' ? 'monitor' : 'phone-device';
  const className = `device${selected ? ' selected' : ''}`;

  const handleRowClick = () => {
    if (typeof onSelect === 'function') onSelect(peer.userId);
  };
  const handleBeamClick = (e) => {
    e.stopPropagation();
    if (typeof onBeam === 'function') onBeam(peer.userId);
  };
  const handleKey = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && typeof onSelect === 'function') {
      e.preventDefault();
      onSelect(peer.userId);
    }
  };

  return (
    <div
      className={className}
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      aria-pressed={onSelect ? selected : undefined}
      onClick={onSelect ? handleRowClick : undefined}
      onKeyDown={onSelect ? handleKey : undefined}
      data-user-id={peer.userId}
    >
      <div className="dev-ico">
        <Icon name={iconName} size={18} stroke="var(--lan, #3fdc8a)" />
      </div>
      <div className="dev-meta">
        <b>{peer.handle}</b>
        <span className="dev-meta-row">
          <span className="dev-meta-text">{description}</span>
          {peer.sameLAN ? <SameLANBadge /> : null}
        </span>
      </div>
      <Signal bars={peer.signalBars} />
      <button
        type="button"
        className="connect"
        onClick={handleBeamClick}
        aria-label={`Beam to ${peer.handle}`}
      >
        <Icon name="zap" size={13} stroke="#06371f" /> Beam
      </button>
    </div>
  );
};

export default DeviceRow;
export { formatRelativeTime };
